/**
 * HMRC Form Mapping Service
 *
 * Maps estate entries to the HMRC forms they are relevant to.
 * Shows users which assets/gifts/items feed into each required form
 * and what evidence is typically needed.
 *
 * Pure function — no React, no side effects.
 * Does NOT change any tax calculations or form detection logic.
 */

import type {
    EstateAssets,
    EstateProfile,
    IHTCalculation,
} from '../../types';
import { resolveResidenceForConsumers } from './rnrbResidenceFacts';

// ── Types ──────────────────────────────────────────────────────────────

export type FormItemType =
    | 'property'
    | 'bank_account'
    | 'investment'
    | 'pension'
    | 'gift'
    | 'debt'
    | 'ownership'
    | 'relief'
    | 'estate_summary'
    | 'probate_fact';

export interface FormRelatedItem {
    itemType: FormItemType;
    label: string;
    itemId?: string;
}

export interface FormMapping {
    formCode: string;
    formName: string;
    triggerReason: string;
    relatedItems: FormRelatedItem[];
    suggestedEvidence: string[];
}

// ── Helpers ────────────────────────────────────────────────────────────

const fmt = (n: number | null | undefined) => {
    if (n == null) return '—';
    return '£' + Math.round(n).toLocaleString('en-GB');
};

// ── Mapping builders ───────────────────────────────────────────────────

function mapIHT400(
    assets: EstateAssets,
    profile: EstateProfile,
    ihtCalc?: IHTCalculation | null,
): FormMapping {
    const b = ihtCalc?.breakdown;
    const items: FormRelatedItem[] = [];

    // Estate summary values
    if (b?.gross_value != null) {
        items.push({ itemType: 'estate_summary', label: `Gross estate: ${fmt(b.gross_value)}` });
    }
    if (b?.net_value != null) {
        items.push({ itemType: 'estate_summary', label: `Net estate: ${fmt(b.net_value)}` });
    }
    if ((b?.debts_total ?? 0) > 0) {
        items.push({ itemType: 'debt', label: `Total debts and liabilities: ${fmt(b!.debts_total)}` });
    }
    for (const d of assets.debts ?? []) {
        if (d.estimated_value && d.estimated_value > 0) {
            items.push({
                itemType: 'debt',
                label: `${d.institution || d.label || 'Debt'}: ${fmt(d.estimated_value)}`,
                itemId: d.id,
            });
        }
    }
    if ((b?.spouse_exemption_total ?? 0) > 0) {
        items.push({ itemType: 'relief', label: `Spouse/civil partner exemption: ${fmt(b!.spouse_exemption_total)}` });
    }
    if ((b?.charity_exemption_total ?? 0) > 0) {
        items.push({ itemType: 'relief', label: `Charitable exemption: ${fmt(b!.charity_exemption_total)}` });
    }

    return {
        formCode: 'IHT400',
        formName: 'Inheritance Tax account',
        triggerReason: 'The estate does not qualify as an excepted estate — a full IHT return is required.',
        relatedItems: items,
        suggestedEvidence: [
            'Date-of-death valuations for all assets',
            'Confirmation of all debts and liabilities',
            'Death certificate',
            'Grant of representation (once obtained)',
        ],
    };
}

function mapIHT403(assets: EstateAssets): FormMapping {
    const gifts = assets.lifetime_gifts ?? [];
    const items: FormRelatedItem[] = [];

    for (const g of gifts) {
        const dateLabel = g.date ? ` (${g.date})` : '';
        const exemptNote = g.is_regular_from_income ? ' — claimed as regular expenditure from income' : '';
        items.push({
            itemType: 'gift',
            label: `${g.description || 'Gift'}: ${fmt(g.amount)}${dateLabel}${exemptNote}`,
            itemId: g.id,
        });
    }

    return {
        formCode: 'IHT403',
        formName: 'Gifts and other transfers of value',
        triggerReason: `${gifts.length} gift${gifts.length !== 1 ? 's' : ''} within 7 years of death must be reported.`,
        relatedItems: items,
        suggestedEvidence: [
            'Bank statements confirming each transfer',
            'Written confirmation from each recipient',
            'Documentation supporting any exemption claimed (e.g. annual exemption, normal expenditure out of income)',
            'Income and expenditure records if s21 exemption is relied on',
        ],
    };
}

function mapIHT404(assets: EstateAssets): FormMapping {
    const items: FormRelatedItem[] = [];

    for (const p of assets.properties ?? []) {
        if (p.ownership_type === 'joint_tenants' || p.ownership_type === 'tenants_in_common') {
            const share = p.deceased_share_percent != null ? ` (${p.deceased_share_percent}% share)` : '';
            items.push({
                itemType: 'property',
                label: `${p.label || p.address || 'Property'} — ${p.ownership_type === 'joint_tenants' ? 'joint tenants' : 'tenants in common'}${share}`,
                itemId: p.id,
            });
        }
    }

    for (const b of assets.bank_accounts ?? []) {
        if (b.ownership === 'joint') {
            items.push({
                itemType: 'bank_account',
                label: `${b.provider || b.label || 'Account'} — joint account`,
                itemId: b.id,
            });
        }
    }

    for (const inv of assets.investments ?? []) {
        if (inv.ownership === 'joint') {
            items.push({
                itemType: 'investment',
                label: `${inv.provider || inv.label || 'Investment'} — joint holding`,
                itemId: inv.id,
            });
        }
    }

    return {
        formCode: 'IHT404',
        formName: 'Jointly owned assets',
        triggerReason: `${items.length} jointly owned asset${items.length !== 1 ? 's' : ''} in the estate.`,
        relatedItems: items,
        suggestedEvidence: [
            'Land Registry title register (for joint property)',
            'Bank account mandates showing joint ownership',
            'Declaration of trust (if beneficial shares differ from legal ownership)',
            'Evidence of the deceased\'s beneficial share',
        ],
    };
}

function mapIHT405(assets: EstateAssets): FormMapping {
    const items: FormRelatedItem[] = [];

    for (const p of assets.properties ?? []) {
        const fullVal = Number(p.estimated_value) || 0;
        const sharePct = p.deceased_share_percent;
        const mortgage = p.has_mortgage ? (Number(p.mortgage_balance) || 0) : 0;
        // Show the FULL property value labelled as such, and the deceased's
        // share value separately — never attach the "% share" to the full value.
        let valuePart = '';
        if (fullVal > 0) {
            if (sharePct != null && sharePct < 100) {
                const shareVal = Math.round(Math.max(0, fullVal - mortgage) * (sharePct / 100));
                valuePart = ` — full value ${fmt(fullVal)}, deceased's ${sharePct}% share ${fmt(shareVal)}`;
            } else {
                valuePart = ` — ${fmt(fullVal)}`;
            }
        }
        const ownershipDesc = p.ownership_type === 'sole' ? 'sole ownership'
            : p.ownership_type === 'joint_tenants' ? 'joint tenants'
            : p.ownership_type === 'tenants_in_common' ? 'tenants in common'
            : 'ownership TBC';
        items.push({
            itemType: 'property',
            label: `${p.label || p.address || 'Property'} — ${ownershipDesc}${valuePart}`,
            itemId: p.id,
        });
    }

    return {
        formCode: 'IHT405',
        formName: 'Houses, land, buildings and interests in land',
        triggerReason: `${assets.properties.length} propert${assets.properties.length !== 1 ? 'ies' : 'y'} in the estate.`,
        relatedItems: items,
        suggestedEvidence: [
            'Professional date-of-death valuation (RICS Red Book recommended)',
            'Land Registry title register for each property',
            'Mortgage redemption statement (if mortgage exists)',
            'Tenancy agreements (if property is let)',
        ],
    };
}

function mapIHT406(assets: EstateAssets): FormMapping {
    const items: FormRelatedItem[] = [];

    for (const b of assets.bank_accounts ?? []) {
        const val = b.estimated_value ? ` — ${fmt(b.estimated_value)}` : '';
        const ownership = b.ownership === 'joint' ? ' (joint)' : b.ownership === 'sole' ? ' (sole)' : '';
        items.push({
            itemType: 'bank_account',
            label: `${b.provider || b.label || 'Account'}${ownership}${val}`,
            itemId: b.id,
        });
    }

    return {
        formCode: 'IHT406',
        formName: 'Bank and building society accounts',
        triggerReason: `${assets.bank_accounts.length} bank account${assets.bank_accounts.length !== 1 ? 's' : ''} in the estate.`,
        relatedItems: items,
        suggestedEvidence: [
            'Official date-of-death balance confirmation from each bank',
            'Interest accrued to date of death',
            'Account mandates (for joint accounts)',
        ],
    };
}

function mapIHT411(assets: EstateAssets): FormMapping {
    const items: FormRelatedItem[] = [];

    for (const inv of assets.investments ?? []) {
        const val = inv.estimated_value ? ` — ${fmt(inv.estimated_value)}` : '';
        items.push({
            itemType: 'investment',
            label: `${inv.provider || inv.label || 'Investment'}${val}`,
            itemId: inv.id,
        });
    }

    return {
        formCode: 'IHT411',
        formName: 'Listed stocks and shares',
        triggerReason: `${assets.investments.length} investment holding${assets.investments.length !== 1 ? 's' : ''} recorded — listed/quoted securities should be reported.`,
        relatedItems: items,
        suggestedEvidence: [
            'Portfolio valuation at date of death from each provider',
            'Stockbroker statement showing holdings and values',
            'Share certificates (if held in certificated form)',
            'Dividend income accrued to date of death',
        ],
    };
}

/**
 * IHT435 questions 4 and 5 (AUTH-HMRC-FORM-IHT435 snapshot, "Details of the residence on which you are claiming RNRB"):
 *   Q4 "Does the estate include any residential property that the deceased owned and lived in?" (note 4: ever lived
 *      in as their residence while they owned it — the s.8H(2) residence-history fact);
 *   Q5 "What's the address of the residence?" (note 5: the residence RNRB is claimed on; with more than one, the
 *      nominated one).
 * Both come only from the shared Stage-1 resolution (resolveResidenceForConsumers), never from is_main_residence.
 * Q5 names a property only for a confirmed QRI. A legacy provisional QRI, a missing nomination or an unknown residence
 * history withholds it (no property is chosen); with no current RPI (s.8FB) no current address is given.
 */
function mapIHT435(
    assets: EstateAssets,
    profile: EstateProfile | null | undefined,
    ihtCalc?: IHTCalculation | null,
): FormMapping {
    const b = ihtCalc?.breakdown;
    const items: FormRelatedItem[] = [];
    const residence = resolveResidenceForConsumers(assets.properties, profile?.iht_details);
    const name = (p: { label?: string | null; address?: string | null }) => p.label || p.address || 'Property';
    const names = (ps: Array<{ label?: string | null; address?: string | null }>) => ps.map(name).join(', ');
    // An explicit v2 'yes' on a current property answers Q4 even while Q5 is open (a nomination or another history).
    const knownRpi = residence.mode === 'v2' && residence.candidates.some((p) => p.has_been_residence === 'yes');

    if (residence.qri && !residence.provisional) {
        const home = residence.qri;
        items.push({ itemType: 'probate_fact', label: 'Q4 Residential property owned and lived in: Yes' });
        const val = home.estimated_value ? ` — ${fmt(home.estimated_value)}` : '';
        items.push({
            itemType: 'property',
            label: `Qualifying residence: ${home.label || home.address || 'Residence'}${val}`,
            itemId: home.id,
        });

        // Inheritance destination
        if (home.deceased_share_passes_to) {
            const dest = home.deceased_share_passes_to === 'direct_descendants' ? 'direct descendants'
                : home.deceased_share_passes_to === 'spouse' ? 'spouse/civil partner'
                : home.deceased_share_passes_to;
            items.push({
                itemType: 'probate_fact',
                label: `Property passes to: ${dest}`,
            });
        }

        if (home.beneficiary_allocations?.length) {
            for (const alloc of home.beneficiary_allocations) {
                const typeLabel = alloc.type === 'direct_descendant' ? 'Direct descendant'
                    : alloc.type === 'spouse' ? 'Spouse/civil partner'
                    : alloc.type === 'charity' ? 'Charity'
                    : 'Other';
                items.push({
                    itemType: 'probate_fact',
                    label: `${typeLabel}: ${alloc.percentage}% of deceased's share`,
                });
            }
        }
    } else if (residence.qri) {
        items.push({
            itemType: 'probate_fact',
            label: `Q4 and Q5 not yet confirmed: ${name(residence.qri)} is used provisionally as the residence. Confirm they lived in it as their home before entering its address.`,
        });
    } else if (residence.nominationRequired) {
        if (knownRpi) items.push({ itemType: 'probate_fact', label: 'Q4 Residential property owned and lived in: Yes' });
        items.push({
            itemType: 'probate_fact',
            label: `Q5 address withheld: a qualifying residence has not yet been nominated (possible residences: ${names(residence.candidates)}).`,
        });
    } else if (residence.residenceHistoryUnknown) {
        if (knownRpi) items.push({ itemType: 'probate_fact', label: 'Q4 Residential property owned and lived in: Yes' });
        items.push({
            itemType: 'probate_fact',
            label: `Q5 address withheld: whether they lived in ${names(residence.candidates.filter((p) => !(residence.mode === 'v2' && p.has_been_residence === 'yes')))} as their home is not yet known.`,
        });
    } else {
        items.push({
            itemType: 'probate_fact',
            label: 'Q4 No property in the estate at death is recorded as one they lived in, so no residence address is given at Q5. Any RNRB rests on the downsizing questions (15 to 24).',
        });
    }

    if ((b?.rnrb_available ?? 0) > 0) {
        items.push({
            itemType: 'relief',
            label: `RNRB applied: ${fmt(b!.rnrb_available)}`,
        });
    }

    return {
        formCode: 'IHT435',
        formName: 'Claim for Residence Nil Rate Band',
        triggerReason: residence.noCurrentRpi
            ? 'The Residence Nil Rate Band is being claimed through the downsizing addition — no residence is recorded in the estate at death.'
            : 'The Residence Nil Rate Band is being claimed — a qualifying residence passes to direct descendants.',
        relatedItems: items,
        suggestedEvidence: [
            'Will or intestacy provision showing the property passes to direct descendants',
            'Evidence of qualifying residence (Land Registry title, council tax records)',
            'Property valuation at date of death',
            'Evidence of the deceased\'s share if jointly owned',
        ],
    };
}

function mapPA1P(
    assets: EstateAssets,
    profile: EstateProfile,
): FormMapping {
    const items: FormRelatedItem[] = [];

    items.push({
        itemType: 'probate_fact',
        label: `Will exists: ${profile.representatives?.will_exists === 'yes' ? 'Yes' : profile.representatives?.will_exists === 'no' ? 'No' : 'Not confirmed'}`,
    });

    const props = assets.properties ?? [];
    const hasSole = props.some(p => p.ownership_type === 'sole');
    const hasTic = props.some(p => p.ownership_type === 'tenants_in_common');
    if (hasSole || hasTic) {
        items.push({
            itemType: 'probate_fact',
            label: `Probate needed for: ${[hasSole ? 'solely owned property' : '', hasTic ? 'tenants in common property' : ''].filter(Boolean).join(', ')}`,
        });
    }

    const soleBank = assets.bank_accounts.filter(b => b.ownership === 'sole');
    if (soleBank.length > 0) {
        items.push({
            itemType: 'probate_fact',
            label: `${soleBank.length} solely held bank account${soleBank.length !== 1 ? 's' : ''} (providers may require grant)`,
        });
    }

    return {
        formCode: profile.representatives?.will_exists === 'no' ? 'PA1A' : 'PA1P',
        formName: profile.representatives?.will_exists === 'no'
            ? 'Probate application (no will — letters of administration)'
            : 'Probate application (with a will)',
        triggerReason: 'Probate is likely required to administer the estate.',
        relatedItems: items,
        suggestedEvidence: [
            'Original signed will and any codicils (if applicable)',
            'Certified copy of the death certificate',
            'IHT421 probate summary (if IHT400 route)',
            'Executor identification documents',
        ],
    };
}

// ── Main Generator ─────────────────────────────────────────────────────

export function generateFormMappings(
    assets: EstateAssets,
    profile: EstateProfile,
    routeKey: string | null | undefined,
    ihtCalc?: IHTCalculation | null,
): FormMapping[] {
    const isIHT400 = routeKey === 'POST_2022_IHT400';
    const mappings: FormMapping[] = [];

    if (!isIHT400) return mappings;

    // IHT400 — always present for IHT400 route
    mappings.push(mapIHT400(assets, profile, ihtCalc));

    // IHT403 — gifts
    if ((assets.lifetime_gifts ?? []).length > 0 || assets.gifts_last_7_years?.exists === true) {
        mappings.push(mapIHT403(assets));
    }

    // IHT404 — joint assets
    const hasJoint =
        assets.properties.some(p => p.ownership_type === 'joint_tenants' || p.ownership_type === 'tenants_in_common') ||
        assets.bank_accounts.some(b => b.ownership === 'joint') ||
        assets.investments.some(i => i.ownership === 'joint');
    if (hasJoint) {
        mappings.push(mapIHT404(assets));
    }

    // IHT405 — property
    if (assets.properties.length > 0) {
        mappings.push(mapIHT405(assets));
    }

    // IHT406 — bank accounts
    if (assets.bank_accounts.length > 0) {
        mappings.push(mapIHT406(assets));
    }

    // IHT411 — investments
    if (assets.investments.length > 0) {
        mappings.push(mapIHT411(assets));
    }

    // IHT435 — RNRB
    const b = ihtCalc?.breakdown;
    if ((b?.rnrb_available ?? 0) > 0 || (b?.rnrb_before_taper ?? 0) > 0) {
        mappings.push(mapIHT435(assets, profile, ihtCalc));
    }

    // PA1P / PA1A — probate application
    const probateRequired =
        assets.properties.some(p => p.ownership_type === 'sole' || p.ownership_type === 'tenants_in_common') ||
        assets.bank_accounts.some(b => b.ownership === 'sole') ||
        assets.investments.some(i => i.ownership === 'sole');
    if (probateRequired) {
        mappings.push(mapPA1P(assets, profile));
    }

    return mappings;
}
